import { useMemo, useState } from 'react'

import { AgChartOptions } from 'ag-charts-community'

import { CustomAgChart } from '@fortem-monorepo/react-ag'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { DataTableFacetedFilter } from '@fortem-monorepo/react-ui'

import { useEnums } from '@/pages/flow/pricing-matrix/use-enums'
import { sharedOptions } from '@/pages/flow/rfq/components/market-data-options'
import { useRFQPricingMatrixQuery } from '@/react-query/queries/rfq'

export interface PricingMatrixTableProps {
  options?: Omit<AgChartOptions, 'series' | 'axes'>
}

export const PricingMatrixTable: React.FC<PricingMatrixTableProps> = ({ options = {} }) => {
  const [_banks, setBanks] = useState<string[]>([])
  const [_products, setProducts] = useState<string[]>([])
  const [_underlyings, setUnderlyings] = useState<string[]>([])
  const [_types, setTypes] = useState<string[]>([])

  const query = useRFQPricingMatrixQuery()

  const data = useMemo(() => {
    return (query.data ?? []).map((item) => ({
      ...item,
    }))
  }, [query.data])

  const formattedData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      Date: new Date(item.Date),
    }))
  }, [data])

  const { banks, products, types, underlyings } = useEnums(data)

  const filteredData = useMemo(() => {
    return formattedData.filter((item) => {
      const hasBank =
        _banks.length === 0 ||
        Object.entries(item.banks).filter(
          ([bankName, bankValue]) => _banks.includes(bankName) && !!bankValue,
        )

      const hasProduct = _products.length === 0 || _products.includes(item.type!)

      const itemUnderlyings = (item?.underlyings ?? '').split(', ')

      const hasUnderlyings =
        _underlyings.length === 0 ||
        _underlyings.some((pair) =>
          pair.split(', ').every((underlying) => itemUnderlyings.includes(underlying)),
        )

      const hasType = _types.length === 0 || _types.includes(item['Live/Indicative'] ?? '')

      return [hasBank, hasProduct, hasUnderlyings, hasType].every((check) => check)
    })
  }, [formattedData, _banks, _products, _underlyings, _types])

  const mergedOptions: AgChartOptions = {
    ...sharedOptions,
    series: _banks.map((bank) => {
      return {
        type: 'scatter',
        data: filteredData,
        xKey: 'Date',
        yKey: `banks.${bank}`,
        yName: bank,
      }
    }),
    axes: [
      {
        type: 'number',
        position: 'left',
      },
      {
        type: 'unit-time',
        parentLevel: {
          enabled: true,
        },
        position: 'bottom',
        keys: ['Date'],
        crosshair: {
          enabled: true,
          snap: false,
        },
      },
    ],
    ...options,
  }

  return (
    <Widget>
      <WidgetHeaderWithQuery query={query} title="Pricing Matrix">
        <div className={'flex items-center gap-2 px-3 py-2 border-t'}>
          <DataTableFacetedFilter
            size="sm"
            title={'Bank'}
            values={_banks}
            setValues={setBanks}
            options={banks.map((bank) => ({
              label: bank,
              value: bank,
            }))}
          />
          <DataTableFacetedFilter
            size="sm"
            title={'Product'}
            values={_products}
            setValues={setProducts}
            options={products.map((product) => ({
              label: product,
              value: product,
            }))}
          />
          <DataTableFacetedFilter
            size="sm"
            title={'Underlying'}
            values={_underlyings}
            setValues={setUnderlyings}
            options={underlyings.map((under) => ({
              label: under,
              value: under,
            }))}
          />
          <DataTableFacetedFilter
            size="sm"
            title={'Type'}
            values={_types}
            setValues={setTypes}
            options={types.map((type) => ({
              label: type,
              value: type,
            }))}
          />
        </div>
      </WidgetHeaderWithQuery>
      <WidgetContent>
        <CustomAgChart options={mergedOptions} />
      </WidgetContent>
    </Widget>
  )
}
